import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import type { StayPlanModule } from "@/lib/daycare-hotel/stayPlans";
import { nightsBetween } from "@/lib/daycare-hotel/stayDays";

export interface CheckOutStayInput {
  stayDayId: string;
  deliveredItemIds: string[];
  module: StayPlanModule;
  reservationId: string;
  /** Data em que o checkout está sendo feito, para calcular noites não usadas. */
  checkoutDate: string;
}

/** Hora local `HH:mm`, que é o formato de `check_out_time`. */
const nowHourMinute = () =>
  new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

/**
 * Fecha a saída do pet no dia. O item conferido sai da lista de guardados
 * (`is_kept = false`); o não conferido continua na clínica.
 *
 * Os dias anteriores de um hotel permanecem `checked_in`: é o registro de que
 * o pet esteve presente naqueles dias.
 *
 * No hotel, o check-in já debitou TODAS as noites da reserva de uma vez
 * (Task 11). Um checkout antecipado — antes do `check_out_date` original —
 * devolve ao plano as noites que o pet não chegou a usar.
 */
export async function checkOutStayDay({
  stayDayId, deliveredItemIds, now, module, reservationId, checkoutDate,
}: CheckOutStayInput & { now: string }): Promise<void> {
  if (deliveredItemIds.length > 0) {
    const { error } = await supabase
      .from("pet_stored_items")
      .update({ is_kept: false })
      .in("id", deliveredItemIds);
    if (error) throw error;
  }

  if (module === "hotel") {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: reservation, error: reservationError } = await (supabase as any)
      .from("stay_reservations")
      .select("check_out_date")
      .eq("id", reservationId)
      .single();
    if (reservationError) throw reservationError;

    const originalCheckOutDate: string | null = reservation?.check_out_date ?? null;
    const unusedNights = originalCheckOutDate && checkoutDate < originalCheckOutDate
      ? nightsBetween(checkoutDate, originalCheckOutDate)
      : [];

    for (const night of unusedNights) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: refundError } = await (supabase as any).rpc("release_stay_plan_units", {
        p_module: module,
        p_reservation_id: reservationId,
        p_used_on: night,
        p_source: "checkin",
      });
      if (refundError) throw refundError;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase as any)
    .from("stay_reservation_days")
    .update({ status: "checked_out", check_out_time: now })
    .eq("id", stayDayId);
  if (error) throw error;
}

export function useCheckOutStay() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: (input: CheckOutStayInput) => checkOutStayDay({ ...input, now: nowHourMinute() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["panel-day", clinicId] });
      queryClient.invalidateQueries({ queryKey: ["stay-schedule", clinicId] });
      queryClient.invalidateQueries({ queryKey: ["stay-plan-contracts"] });
      queryClient.invalidateQueries({ queryKey: ["patient-history-feed"] });
      queryClient.invalidateQueries({ queryKey: ["owner-history-feed"] });
      queryClient.invalidateQueries({ queryKey: ["pet-reservation-summary"] });
    },
  });
}
